/* eslint-disable no-async-promise-executor */
import { ActionContext } from 'vuex';

import { localesCodes, localesDefault } from '@/config/locales';

interface ApiUiSettings {
    UserLanguageCode?: string;
}
interface UiMobileItem {
    isOpen: boolean;
}
interface UiSearchMobileItem extends UiMobileItem {
    isSearchInputVisiable: boolean;
}
interface UiUserModalMobileItem extends UiMobileItem {
    isUserModalVisiable: boolean;
}

interface UiSettingState {
    settings: ApiUiSettings;
    isMobileMenuOpen: boolean;
    isTermsAndConditionsModalOpen: boolean;
    isNotificationMessage: boolean;
    toolbarState: {
        isSearchLocationVisible: boolean;
        isMapLayersVisible: boolean;
        isDeviceLayersVisible: boolean;
        isDeviceGeofenciesVisible: boolean;
    };
    mobileMenuState: {
        deviceList: UiMobileItem;
        layers: UiMobileItem;
        geofencies: UiMobileItem;
        mapType: UiMobileItem;
        searchSettings: UiSearchMobileItem;
        profileSettings: UiUserModalMobileItem;
    };
}

const ui = {
    namespaced: true,
    state: {
        settings: {},
        isMobileMenuOpen: false,
        isTermsAndConditionsModalOpen: false,
        isNotificationMessage: true,
        toolbarState: {
            isSearchLocationVisible: false,
            isMapLayersVisible: false,
            isDeviceLayersVisible: false,
            isDeviceGeofenciesVisible: false,
        },
        mobileMenuState: {
            deviceList: {
                isOpen: false,
            },
            layers: {
                isOpen: false,
            },
            geofencies: {
                isOpen: false,
            },
            mapType: {
                isOpen: false,
            },
            searchSettings: {
                isOpen: false,
                isSearchInputVisiable: false,
            },
            profileSettings: {
                isOpen: false,
                isUserModalVisiable: false,
            },
        },
    },
    getters: {
        languageCode(state: UiSettingState): string {
            if (
                typeof state.settings.UserLanguageCode !== 'undefined' &&
                localesCodes.includes(state.settings.UserLanguageCode.toLowerCase())
            ) {
                return state.settings.UserLanguageCode.toLowerCase();
            }
            return localesDefault;
        },
        isMobileMenuOpen(state: UiSettingState): boolean {
            return state.isMobileMenuOpen;
        },
        isTermsAndConditionsModalOpen(state: UiSettingState): boolean {
            return state.isTermsAndConditionsModalOpen;
        },
        isNotificationMessage(state: UiSettingState): boolean {
            return state.isNotificationMessage;
        },
        isDeviceListSubmenuOpen(state: UiSettingState): boolean {
            return state.mobileMenuState.deviceList.isOpen;
        },
        isLayersSubmenuOpen(state: UiSettingState): boolean {
            return state.mobileMenuState.layers.isOpen;
        },
        isGeofenciesSubmenuOpen(state: UiSettingState): boolean {
            return state.mobileMenuState.geofencies.isOpen;
        },
        isMapTypeSubmenuOpen(state: UiSettingState): boolean {
            return state.mobileMenuState.mapType.isOpen;
        },
        isSearchSettingsSubmenuOpen(state: UiSettingState): boolean {
            return state.mobileMenuState.searchSettings.isOpen;
        },
        isProfileSettingsSubmenuOpen(state: UiSettingState): boolean {
            return state.mobileMenuState.profileSettings.isOpen;
        },
        isSearchInputVisible(state: UiSettingState): boolean {
            return state.mobileMenuState.searchSettings.isSearchInputVisiable;
        },
        isUserModalVisible(state: UiSettingState): boolean {
            return state.mobileMenuState.profileSettings.isUserModalVisiable;
        },
        isToolbarSearchLocationVisible(state: UiSettingState): boolean {
            return state.toolbarState.isSearchLocationVisible;
        },
        isToolbarMapLayersVisible(state: UiSettingState): boolean {
            return state.toolbarState.isMapLayersVisible;
        },
        isToolbarDeviceLayersVisible(state: UiSettingState): boolean {
            return state.toolbarState.isDeviceLayersVisible;
        },
        isToolbarDeviceGeofenciesVisible(state: UiSettingState): boolean {
            return state.toolbarState.isDeviceGeofenciesVisible;
        },
    },
    mutations: {
        updateLanguage(state: UiSettingState, language: string): void {
            state.settings = { UserLanguageCode: language };
        },
        openMobileMenu(state: UiSettingState): void {
            state.isMobileMenuOpen = true;
        },
        closeMobileMenu(state: UiSettingState): void {
            state.isMobileMenuOpen = false;
        },
        triggerMobileMenu(state: UiSettingState): void {
            state.isMobileMenuOpen = !state.isMobileMenuOpen;
        },
        openTermsAndConditionsModal(state: UiSettingState): void {
            state.isTermsAndConditionsModalOpen = true;
        },
        closeTermsAndConditionsModal(state: UiSettingState): void {
            state.isTermsAndConditionsModalOpen = false;
        },
        openNotificationMessage(state: UiSettingState): void {
            state.isNotificationMessage = true;
        },
        closeNotificationMessage(state: UiSettingState): void {
            state.isNotificationMessage = false;
        },
        triggerDeviceListSubmenu(state: UiSettingState): void {
            state.mobileMenuState.deviceList.isOpen = !state.mobileMenuState.deviceList.isOpen;
        },
        triggerLayersSubmenu(state: UiSettingState): void {
            state.mobileMenuState.layers.isOpen = !state.mobileMenuState.layers.isOpen;
        },
        triggerGeofenciesSubmenu(state: UiSettingState): void {
            state.mobileMenuState.geofencies.isOpen = !state.mobileMenuState.geofencies.isOpen;
        },
        triggerMapTypeSubmenu(state: UiSettingState): void {
            state.mobileMenuState.mapType.isOpen = !state.mobileMenuState.mapType.isOpen;
        },
        triggerSearchSettingsSubmenu(state: UiSettingState): void {
            state.mobileMenuState.searchSettings.isOpen = !state.mobileMenuState.searchSettings.isOpen;
        },
        triggerProfileSettingsSubmenu(state: UiSettingState): void {
            state.mobileMenuState.profileSettings.isOpen = !state.mobileMenuState.profileSettings.isOpen;
        },
        triggerSearchInputVisibility(state: UiSettingState): void {
            state.mobileMenuState.searchSettings.isSearchInputVisiable =
                !state.mobileMenuState.searchSettings.isSearchInputVisiable;
        },
        openUserModalVisibility(state: UiSettingState): void {
            state.mobileMenuState.profileSettings.isUserModalVisiable = true;
        },
        closeUserModalVisibility(state: UiSettingState): void {
            state.mobileMenuState.profileSettings.isUserModalVisiable = false;
        },
        triggerToolbarSearchLocationVisibility(state: UiSettingState): void {
            state.toolbarState.isSearchLocationVisible = !state.toolbarState.isSearchLocationVisible;
        },
        showToolbarSearchLocationVisibility(state: UiSettingState): void {
            state.toolbarState.isSearchLocationVisible = true;
        },
        closeToolbarSearchLocationVisibility(state: UiSettingState): void {
            state.toolbarState.isSearchLocationVisible = false;
        },
        triggerToolbarMapLayersVisibility(state: UiSettingState): void {
            state.toolbarState.isMapLayersVisible = !state.toolbarState.isMapLayersVisible;
        },
        showToolbarMapLayersVisibility(state: UiSettingState): void {
            state.toolbarState.isMapLayersVisible = true;
        },
        closeToolbarMapLayersVisibility(state: UiSettingState): void {
            state.toolbarState.isMapLayersVisible = false;
        },
        triggerToolbarDeviceLayersVisibility(state: UiSettingState): void {
            state.toolbarState.isDeviceLayersVisible = !state.toolbarState.isDeviceLayersVisible;
        },
        showToolbarDeviceLayersVisibility(state: UiSettingState): void {
            state.toolbarState.isDeviceLayersVisible = true;
        },
        closeToolbarDeviceLayersVisibility(state: UiSettingState): void {
            state.toolbarState.isDeviceLayersVisible = false;
        },
        triggerToolbarDeviceGeofenciesVisibility(state: UiSettingState): void {
            state.toolbarState.isDeviceGeofenciesVisible = !state.toolbarState.isDeviceGeofenciesVisible;
        },
        showToolbarDeviceGeofenciesVisibility(state: UiSettingState): void {
            state.toolbarState.isDeviceGeofenciesVisible = true;
        },
        closeToolbarDeviceGeofenciesVisibility(state: UiSettingState): void {
            state.toolbarState.isDeviceGeofenciesVisible = false;
        },
        clearUiSettings(state: UiSettingState): void {
            state.settings = {};
            state.isMobileMenuOpen = false;
            state.isTermsAndConditionsModalOpen = false;
            state.isNotificationMessage = false;
            (state.toolbarState = {
                isSearchLocationVisible: false,
                isMapLayersVisible: false,
                isDeviceLayersVisible: false,
                isDeviceGeofenciesVisible: false,
            }),
                (state.mobileMenuState = {
                    deviceList: {
                        isOpen: false,
                    },
                    layers: {
                        isOpen: false,
                    },
                    geofencies: {
                        isOpen: false,
                    },
                    mapType: {
                        isOpen: false,
                    },
                    searchSettings: {
                        isOpen: false,
                        isSearchInputVisiable: false,
                    },
                    profileSettings: {
                        isOpen: false,
                        isUserModalVisiable: false,
                    },
                });
        },
    },
    actions: {
        changeLanguage(context: ActionContext<UiSettingState, null>, language: string): void {
            context.commit('updateLanguage', language);
        },
        clearLanguage(context: ActionContext<UiSettingState, null>): void {
            context.commit('clearUiSettings');
        },
    },
};

export default ui;
